import React, { useState, useEffect } from 'react';
import { Offcanvas } from 'react-bootstrap';
import './index.scss';

import { APIS } from "../../../config/apis";
import { REQUEST_METHOD, useBodyScrollLock, useModalEscape } from '@laramie/ui';
import { standardRequest } from "../../../utils/requests";

// `position` y `compact` se mantienen en la firma por compatibilidad con los call-sites,
// pero ya no tienen efecto (antes configuraban el popover).
// Modo por defecto: se busca el contenido por `name` (código del tip) contra el backend.
// Modo inline: si se pasa `content` (HTML), se usa directo y no se hace fetch — para
// consumidores que ya tienen el texto en memoria (ej: la ayuda de cada función del asistente).
function Tip({ name, content, title: titleProp, link: linkProp, position = 'top', compact = false }) {
    const [showTip, setShowTip] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [link, setLink] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Modo inline: el contenido (HTML) llega por props; no se busca por código.
        if (content != null) {
            setTitle(titleProp || 'Ayuda');
            setDescription(content);
            setLink((linkProp || '').trim());
            return;
        }
        if (!name) return;
        setLoading(true);

        standardRequest(
            REQUEST_METHOD.GET,
            APIS.URLS.TIP + `/codigo/${name}?modulo=${APIS.MODULO}`,
            null,
            { disableToast: true }
        )
            .then(data => {
                setTitle(data.titulo || 'Ayuda');
                setDescription(data.descripcion || 'Sin contenido');
                setLink((data.link || '').trim());
            })
            .catch(() => {
                setTitle('Error');
                setDescription('No se pudo obtener la información.');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [name, content, titleProp, linkProp]);

    // ESC: se integra al stack de escape del proyecto (el mismo que usan los modales).
    // Cuando el tip se abre desde un modal, se asocia a ese modal para recibir el ESC
    // antes que él: el primer ESC cierra el drawer y deja el modal abierto.
    useModalEscape(
        () => setShowTip(false),
        showTip,
        () => {
            const modals = document.querySelectorAll('.modal.modal-block');
            return modals.length ? modals[modals.length - 1] : null;
        }
    );

    // Bloquea el scroll del view de fondo con el drawer abierto, con el mismo lock que
    // usan los modales: al compartir contador, abierto sobre un modal no re-lockea.
    useBodyScrollLock(showTip);

    const toggleTip = () => setShowTip((s) => !s);

    return (
        <div className="tip-wrapper">
            <span
                className="material-symbols-outlined ms-help help-button"
                onClick={toggleTip}
                role="button"
                tabIndex={0}
                aria-label="Abrir ayuda"
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleTip();
                    }
                }}
            >
                help
            </span>

            {/* scroll: mantiene apagados el scroll-lock y el enforceFocus de react-bootstrap
                (enforceFocus = enforceFocus && !scroll): su lock no compensa los fixed del
                proyecto (.footer, .modal-block) y su trap de foco pelearía con modalFocusTrap.
                El bloqueo del scroll lo hace useBodyScrollLock (arriba). */}
            <Offcanvas
                show={showTip}
                onHide={() => setShowTip(false)}
                placement="end"
                className="tip-offcanvas"
                keyboard={false}
                scroll
                aria-label="Ayuda"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="tip-title">
                        {loading ? 'Cargando…' : title}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {loading ? (
                        'Cargando…'
                    ) : (
                        <>
                            <div dangerouslySetInnerHTML={{ __html: description }} />
                            {link && (
                                <a
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="tip-link-cta"
                                >
                                    Ver más
                                </a>
                            )}
                        </>
                    )}
                </Offcanvas.Body>
            </Offcanvas>
        </div>
    );
}

export default Tip;
